import { logoutUser, getLoggedInUser, getDeviceKey, CSRF_TOKEN_HEADER_KEY, getCsrfToken, updateAccessTokenExpirationTime, updateCsrfToken, isAccessTokenExpired, setAuthorities, isAuthenticated } from 'services/AuthService';
import axios from 'axios';
import config from 'config';

const url = (uri) => `${config.apiRoot}${uri}`;

function defaultAuthHeaders() {
  return { [CSRF_TOKEN_HEADER_KEY]: getCsrfToken() };
}

function isCanceledAxiosError(error) {
  return (
    error &&
    (error.code === 'ERR_CANCELED' ||
      error.name === 'CanceledError' ||
      (typeof axios.isCancel === 'function' && axios.isCancel(error)))
  );
}

const parseRequestTimeout = (timeout) => {
  if (timeout === null) return 0;
  const n = Number(timeout);
  return Number.isFinite(n) ? n : 0;
}

export function sendAsyncRequest(uri, method, data, onSuccessCallback, onFailureCallback){
  axios({
    method: method,
    url: `${config.authServiceRoot}${uri}`,
    data: data,
    withCredentials: true
  }).then(function (response) {
    onSuccessCallback(response);
  }).catch(function (error) {
    onFailureCallback(error);
  });
}

function asyncAuthRequestWithUrlResolver(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout, resolveUrl, buildHeaders, alreadyRetriedAfterRefresh = false) {
  const resolveRequestUrl = resolveUrl || url;
  const buildRequestHeaders = buildHeaders || defaultAuthHeaders;

  if (isAccessTokenExpired()) {
    // After a successful refresh, a still-expired local token means auth is unusable — do not loop.
    if (alreadyRetriedAfterRefresh) {
      logoutUser();
      return;
    }
    refreshToken(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout, resolveRequestUrl, buildRequestHeaders);
  } else {
    const axiosConfig = {
      method: method,
      url: resolveRequestUrl(uri),
      data: data,
      responseType: responseType,
      headers: buildRequestHeaders(),
      withCredentials: true,
      signal: signal,
      timeout: parseRequestTimeout(timeout),
    };

    axios(axiosConfig)
      .then(function (response) {
        onSuccessCallback(response);
      })
      .catch(function (error) {
        if (isCanceledAxiosError(error)) {
          return;
        }
        if (error.response && error.response.status === 401) {
          // Auth-service refresh can succeed while platform APIs still return 401
          // (e.g. IP restrictions). Only refresh once per logical request.
          if (alreadyRetriedAfterRefresh) {
            if (typeof onFailureCallback === 'function')
              onFailureCallback(error.response || error);
            else
              console.log(error.response);
            logoutUser();
            return;
          }
          refreshToken(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout, resolveRequestUrl, buildRequestHeaders);
        } else {
          if (typeof onFailureCallback === 'function')
            onFailureCallback(error.response || error);
          else
            console.log(error.response);
        }
      })
      .finally(() => {
        if (signal?.aborted) return;
        if (typeof onFinallyCallback === 'function') onFinallyCallback();
      });
  }
}

function asyncAuthRequest(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout){
  asyncAuthRequestWithUrlResolver(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout, url, defaultAuthHeaders);
}

export function sendAuthenticatedAsyncRequest(uri, method, data, onSuccessCallback, onFailureCallback){
  asyncAuthRequest(uri, method, data, onSuccessCallback, onFailureCallback, null, null, null, null);
}

/**
 * Releases a call-group claim in a way that can outlive tab close. Axios/XHR from
 * React unmount is cancelled by the browser on pagehide; {@code keepalive} is not.
 */
export function unclaimCallGroupKeepAlive(callGroupUuid) {
  if (!callGroupUuid) {
    return;
  }
  const csrf = getCsrfToken();
  fetch(url(`/agent-workspace/${callGroupUuid}/unclaim`), {
    method: "GET",
    credentials: "include",
    keepalive: true,
    headers: csrf ? { [CSRF_TOKEN_HEADER_KEY]: csrf } : {},
  }).catch(() => {});
}

export function sendAuthenticatedAsyncRequestWithResponseType(uri, method, data, onSuccessCallback, onFailureCallback, responseType){
  asyncAuthRequest(uri, method, data, onSuccessCallback, onFailureCallback, responseType, null, null, null);
}

export function sendAuthenticatedAsyncRequestWithFinallyCallback(uri, method, data, onSuccessCallback, onFailureCallback, onFinallyCallback, signal, timeout){
  asyncAuthRequest(uri, method, data, onSuccessCallback, onFailureCallback, null, onFinallyCallback, signal, timeout);
}

function customersApiServiceUrl(uri) {
	return `${config.customersApiServiceRoot}${uri.startsWith('/') ? uri : `/${uri}`}`;
}

function customersApiAuthHeaders() {
  return { [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(), 'Content-Type': 'application/json' };
}

export function sendCustomersApiServiceAuthenticatedRequest(uri, method, data, onSuccessCallback, onFailureCallback) {
  asyncAuthRequestWithUrlResolver(uri, method, data, onSuccessCallback, onFailureCallback, null, null, undefined, undefined, customersApiServiceUrl, customersApiAuthHeaders);
}


export function sendIdentityServiceAuthenticatedRequest(uri, method, data, onSuccessCallback, onFailureCallback) {
  asyncAuthRequestWithUrlResolver(uri, method, data, onSuccessCallback, onFailureCallback, null, null, undefined, undefined, identityServiceUrl, identityServiceAuthHeaders);
}

function identityServiceUrl(uri) {
  return `${config.authServiceRoot}${uri.startsWith('/') ? uri : `/${uri}`}`;
}

function identityServiceAuthHeaders() {
  return { [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(), 'Content-Type': 'application/json' };
}

function refreshToken(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout, resolveUrl, buildHeaders) {
  const user = getLoggedInUser();
  const deviceId = getDeviceKey();
  if (!user || !deviceId) {
    logoutUser();
    return;
  }
  axios({
    method: 'POST',
    url: `${config.authServiceRoot}/auth/refresh-token`,
    data: JSON.stringify({
      email: user?.email,
      deviceId: deviceId
    }),
    headers: { 'Content-Type': 'application/json', 'X-Csrf-Token': getCsrfToken() },
    withCredentials: true
  })
    .then(function (response) {
      const { currentTimeMillis, idTokenDurationSecs, csrfToken } = response.data;
      updateAccessTokenExpirationTime(currentTimeMillis, idTokenDurationSecs);
      if (csrfToken) {
        updateCsrfToken(csrfToken);
      }
      // Mark retry so a subsequent platform 401 fails instead of refreshing forever.
      asyncAuthRequestWithUrlResolver(uri, method, data, onSuccessCallback, onFailureCallback, responseType, onFinallyCallback, signal, timeout, resolveUrl, buildHeaders, true);
    })
    .catch(function (error) {
      if (!error.response || error.response.status === 401) {
        logoutUser();
      } else {
        if (typeof onFailureCallback === 'function')
          onFailureCallback(error.response || error);
        else
          console.log(error.response);
      }
    });
}

export function loadAuthorities() {
  return new Promise((resolve, reject) => {
    asyncAuthRequestWithUrlResolver(
      '/authorities/me',
      'GET',
      null,
      (response) => {
        onAuthoritiesSuccess(response);
        resolve(response);
      },
      (error) => {
        onAuthoritiesFailure(error);
        reject(error);
      },
      null,
      null,
      undefined,
      undefined,
      url,
      defaultAuthHeaders
    );
  });
}

/**
 * Loads authorities after login/password-change. On success runs onSuccess.
 * On failure, clears a half-established session so the user is not redirected into the app.
 */
export function loadAuthoritiesOrLogout(onSuccess) {
  return loadAuthorities()
    .then(onSuccess)
    .catch(() => {
      if (isAuthenticated()) {
        logoutUser();
      }
    });
}

function onAuthoritiesSuccess(response) {
  const { authorities } = response.data;
  setAuthorities(authorities);
}

function onAuthoritiesFailure(error) {
  console.error('Failed to load authorities', error);
  // Survives logout reload so Login can show that the session could not be established
  // (e.g. platform 401 while auth refresh still succeeds).
  try {
    sessionStorage.setItem('loginErrorFlash', 'Unable to complete login. Please try again.');
  } catch (e) {
    // ignore storage failures
  }
}
